import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CalendarClock, Upload, ExternalLink, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';

const scheduleStatusColor = (s) => {
  if (s === 'paid') return 'bg-emerald-100 text-emerald-700';
  if (s === 'overdue') return 'bg-red-100 text-red-700';
  if (s === 'invoiced' || s === 'waiting_payment') return 'bg-amber-100 text-amber-700';
  return 'bg-slate-100 text-slate-600';
};

const scheduleStatusIcon = (s) => {
  if (s === 'paid') return <CheckCircle size={14} className="text-emerald-500" />;
  if (s === 'overdue') return <AlertCircle size={14} className="text-red-500" />;
  if (s === 'invoiced' || s === 'waiting_payment') return <Clock size={14} className="text-amber-500" />;
  return <CalendarClock size={14} className="text-slate-400" />;
};

export default function CustomerPaymentSchedules() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [proofDialog, setProofDialog] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [proofUrl, setProofUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [payDialog, setPayDialog] = useState(false);
  const [payResult, setPayResult] = useState(null);

  const fetchData = () => {
    setLoading(true);
    api.get('/customer/high-ticket/orders')
      .then(r => setOrders(r.data.orders || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

  const handlePay = async (schedule) => {
    try {
      const res = await api.post(`/customer/high-ticket/pay/${schedule.id}`, { amount: schedule.amount });
      setPayResult(res.data);
      setPayDialog(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal memproses pembayaran');
    }
  };

  const handleUploadProof = async () => {
    if (!proofUrl.trim()) { toast.error('Masukkan URL bukti pembayaran'); return; }
    setSubmitting(true);
    try {
      await api.post(`/customer/payment-schedules/${selectedSchedule.id}/payment-proof`, { proof_url: proofUrl });
      toast.success('Bukti pembayaran berhasil dikirim');
      setProofDialog(false);
      setProofUrl('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal mengirim bukti');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--store-primary, #2D6CDF)]" />
    </div>
  );

  return (
    <div data-testid="customer-payment-schedules-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Jadwal Pembayaran</h1>
        <p className="text-sm text-slate-500 mt-1">Termin, DP, dan cicilan dari pembelian Anda</p>
      </div>

      {orders.length === 0 ? (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="py-16 text-center">
            <CalendarClock size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="font-medium text-slate-900 mb-1">Belum ada jadwal pembayaran</h3>
            <p className="text-sm text-slate-500">Pembelian produk dengan termin atau cicilan akan muncul di sini</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {orders.map(order => (
            <Card key={order.id} className="border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-slate-900">{order.product_name || 'Produk High Ticket'}</h3>
                    <p className="text-sm text-slate-500 font-mono">{order.order_number || order.id?.slice(0, 12)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500">Total</p>
                    <p className="font-bold text-slate-900">Rp {fmt(order.total_amount)}</p>
                    <p className="text-xs text-emerald-600">Terbayar: Rp {fmt(order.total_paid)}</p>
                    <p className="text-xs text-red-600">Sisa: Rp {fmt(order.remaining)}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  {(order.payment_schedule || []).map((s, idx) => (
                    <div key={s.id || idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        {scheduleStatusIcon(s.status)}
                        <div>
                          <p className="text-sm font-medium text-slate-800">{s.label || `Termin ${idx + 1}`}</p>
                          <p className="text-xs text-slate-400">
                            Jatuh tempo: {s.due_date ? new Date(s.due_date).toLocaleDateString('id-ID') : '-'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="font-semibold text-slate-900 text-sm">Rp {fmt(s.amount)}</p>
                          <Badge className={`text-xs ${scheduleStatusColor(s.status)}`}>{s.status}</Badge>
                        </div>
                        {(s.status === 'invoiced' || s.status === 'waiting_payment' || s.status === 'pending') && (
                          <div className="flex flex-col gap-1">
                            <Button
                              size="sm"
                              className="text-xs h-7 px-2 bg-[var(--store-primary, #2D6CDF)] hover:bg-[var(--store-primary-hover, #1A5BC4)]"
                              onClick={() => handlePay(s)}
                            >
                              <ExternalLink size={12} className="mr-1" /> Bayar
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs h-7 px-2"
                              onClick={() => { setSelectedSchedule(s); setProofUrl(''); setProofDialog(true); }}
                            >
                              <Upload size={12} className="mr-1" /> Bukti
                            </Button>
                          </div>
                        )}
                        {s.proof_url && (
                          <a href={s.proof_url} target="_blank" rel="noreferrer" className="text-xs text-emerald-600 hover:underline">
                            Lihat bukti
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pay Dialog */}
      <Dialog open={payDialog} onOpenChange={setPayDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Informasi Pembayaran</DialogTitle></DialogHeader>
          {payResult && (
            <div className="space-y-3 py-2">
              <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Pokok:</span><span className="font-semibold">Rp {fmt(payResult.base_amount || payResult.amount)}</span></div>
                {payResult.penalty > 0 && <div className="flex justify-between"><span className="text-red-500">Denda keterlambatan:</span><span className="font-semibold text-red-600">Rp {fmt(payResult.penalty)}</span></div>}
                <div className="flex justify-between border-t pt-2"><span className="font-semibold">Total Bayar:</span><span className="font-bold text-lg">Rp {fmt(payResult.total_to_pay)}</span></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayDialog(false)}>Tutup</Button>
            {payResult?.payment_url && (
              <Button
                className="bg-[var(--store-primary, #2D6CDF)] hover:bg-[var(--store-primary-hover, #1A5BC4)]"
                onClick={() => { window.open(payResult.payment_url, '_blank'); setPayDialog(false); }}
              >
                <ExternalLink size={16} className="mr-2" /> Lanjut Bayar
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Proof Dialog */}
      <Dialog open={proofDialog} onOpenChange={setProofDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Upload Bukti Pembayaran</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-600">Termin: <span className="font-semibold">{selectedSchedule?.label}</span></p>
            <p className="text-sm text-slate-600">Nominal: <span className="font-bold">Rp {fmt(selectedSchedule?.amount)}</span></p>
            <div>
              <Label htmlFor="schedule-proof">URL Bukti Pembayaran</Label>
              <Input
                id="schedule-proof"
                placeholder="https://i.imgur.com/..."
                value={proofUrl}
                onChange={e => setProofUrl(e.target.value)}
                className="mt-1"
              />
              <p className="text-xs text-slate-400 mt-1">Upload ke Imgur/Google Drive, lalu paste URL-nya.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProofDialog(false)}>Batal</Button>
            <Button onClick={handleUploadProof} disabled={submitting} className="bg-[var(--store-primary, #2D6CDF)] hover:bg-[var(--store-primary-hover, #1A5BC4)]">
              {submitting ? 'Mengirim...' : 'Kirim Bukti'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
